import { pgTable, text, serial, integer, boolean, timestamp, decimal, primaryKey, date, json, jsonb, varchar, index, uniqueIndex, unique, real, check } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { relations, sql } from "drizzle-orm";
import { z } from "zod";

// User roles
export const userRoles = ["superadmin", "admin", "coach", "parent"] as const;
export type UserRole = (typeof userRoles)[number];

// User statuses for account approval flow
export const userStatuses = ["active", "pending", "rejected", "suspended"] as const;
export type UserStatus = (typeof userStatuses)[number];

// Admin invitation statuses
export const invitationStatuses = ["pending", "used", "expired", "revoked"] as const;
export type InvitationStatus = (typeof invitationStatuses)[number];

// Subscription plan types
export const subscriptionPlans = ["free", "basic", "pro", "enterprise"] as const;
export type SubscriptionPlan = (typeof subscriptionPlans)[number];

// Locations
export const locations = ["Strongsville", "Solon"] as const;
export type Location = (typeof locations)[number];

// Age groups
export const ageGroups = ["5-8 years", "8+ years"] as const;
export type AgeGroup = (typeof ageGroups)[number];

// Session durations
export const sessionDurations = ["60min", "90min"] as const;
export type SessionDuration = (typeof sessionDurations)[number];

// Fee amounts for each session duration
export const feeAmounts = {
  "60min": "100",
  "90min": "120"
} as const;

// Academies table
export const academies = pgTable("academies", {
  id: serial("id").primaryKey(),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  description: text("description"),
  address: text("address"),
  phone: text("phone"),
  email: text("email"),
  logoUrl: text("logo_url"),
  primaryColor: text("primary_color").default("#1e40af"),
  secondaryColor: text("secondary_color").default("#60a5fa"),
  stripeAccountId: text("stripe_account_id"),
  subscriptionTier: text("subscription_tier", { enum: subscriptionPlans }).notNull().default("free"),
  maxPlayers: integer("max_players").default(200),
  maxCoaches: integer("max_coaches").default(10),
  status: text("status").notNull().default("active"), // active, inactive, suspended
  zelleEmail: text("zelle_email"),  // Zelle payment email for the academy
  zellePhone: text("zelle_phone"),  // Zelle payment phone for the academy
  features: jsonb("features").default({}).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Users table
export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  firebaseUid: text("firebase_uid").unique(), // Firebase User ID
  username: text("username").notNull().unique(),
  password: text("password"), // Made optional as Firebase will handle auth
  email: text("email").notNull().unique(),
  fullName: text("full_name").notNull(),
  role: text("role", { enum: userRoles }).notNull().default("parent"),
  status: text("status").default("pending_verification"),  // active, pending_verification, pending_approval, inactive
  isActive: boolean("is_active").default(true),
  isEmailVerified: boolean("is_email_verified").default(false),
  emailVerificationToken: text("email_verification_token"),
  emailVerificationExpires: timestamp("email_verification_expires"),
  emailStatus: text("email_status", { enum: ["sent", "failed", "pending"] }).default("pending"),
  emailFailureReason: text("email_failure_reason"),
  lastEmailAttempt: timestamp("last_email_attempt"),
  phone: text("phone").unique(),
  address: text("address"),
  profileImage: text("profile_image"),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  lastSignInAt: timestamp("last_sign_in_at"),
  lastSignInIp: text("last_sign_in_ip"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Players table
export const players = pgTable("players", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  dateOfBirth: date("date_of_birth").notNull(),
  ageGroup: text("age_group").$type<string>().notNull(),
  location: text("location", { enum: locations }),
  playerType: text("player_type"),
  parentId: integer("parent_id").references(() => users.id),
  emergencyContact: text("emergency_contact"),
  medicalInformation: text("medical_information"),
  profileImage: text("profile_image"),
  pendingCoachReview: boolean("pending_coach_review").default(false),
  healthNotes: text("health_notes"),
  parentNotes: text("parent_notes"),
  status: text("status").default("active"), // soft-delete flag, used by server/routes/players.ts
  weeklyOversCount: decimal("weekly_overs_count", { precision: 4, scale: 1 }).default("0"),
  // Nullable FK — historically "one primary batch per player." The
  // `batchEnrollments` many-to-many join table below now exists for
  // multi-batch enrollment (twice-weekly kids split across two single-day
  // batches, tournaments, guest play). What this column should mean going
  // forward — still a "primary/home batch" convenience lookup alongside
  // batchEnrollments, or fully superseded by it — is an open design
  // question for the PR that migrates the enroll/unenroll routes, not
  // decided here. This PR only adds the join table; this column and its
  // existing read/write paths are untouched.
  batchId: integer("batch_id").references(() => batches.id),
  // CSV bulk-import tracking (nullable — manual entries leave these NULL)
  importedAt: timestamp("imported_at"),
  importBatchId: text("import_batch_id"),
  // Kid registration fields (parent Add Kid form) — nullable except registeredAt
  ballType: text("ball_type"), // 'Tennis ball' | 'Hard/Leather ball'
  skillLevel: text("skill_level"), // 'Beginner' | 'Intermediate' | 'Advanced'
  preferredDays: text("preferred_days"), // JSON array, e.g. '["Mon","Wed","Fri"]'
  timeSlot: text("time_slot"),
  sessionsPerWeek: integer("sessions_per_week"),
  parentName: text("parent_name"),
  parentPhone: text("parent_phone"),
  parentEmail: text("parent_email"),
  registeredAt: timestamp("registered_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Sessions table
export const sessions = pgTable("sessions", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  title: text("title").notNull(),
  description: text("description"),
  sessionType: text("session_type").notNull(),
  ageGroup: text("age_group").$type<string>().notNull(),
  location: text("location", { enum: locations }).notNull(),
  startTime: timestamp("start_time").notNull(),
  endTime: timestamp("end_time").notNull(),
  coachId: integer("coach_id").references(() => users.id).notNull(),
  maxPlayers: integer("max_players"),
  // Nullable FK — links a scheduled session to the squad it's for, so
  // ageGroup can be derived from the batch instead of a disconnected manual
  // dropdown. NULL for legacy sessions created before this column existed.
  batchId: integer("batch_id").references(() => batches.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  // PR-S1 (session detail/edit/cancel) lifecycle + audit fields. Allowed
  // values: 'scheduled' | 'cancelled' — 'completed' is intentionally out of
  // scope, derive from end_time < now() instead. See
  // drizzle/0007_session_cancel_status.sql for the migration.
  status: text("status").notNull().default("scheduled"),
  cancelledAt: timestamp("cancelled_at"),
  cancelledByUserId: integer("cancelled_by_user_id").references(() => users.id),
});

// Session Attendances
export const sessionAttendances = pgTable("session_attendances", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  sessionId: integer("session_id").references(() => sessions.id).notNull(),
  playerId: integer("player_id").references(() => players.id).notNull(),
  attended: boolean("attended").default(false),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Session Availability - parent responses for upcoming sessions
// status vocabulary standardized on yes/no/maybe (2026-08) — the only
// vocabulary with a real, live write path (server/routes/parent-availability.ts,
// server/routes/kid-dashboard.ts). going/not_going/unsure was the original
// migration's vocabulary but had zero real usage — see DOCS/sprint-log.md.
// Default 'maybe' matches the real deployed SQLite table (scripts/init-sqlite-schema.ts),
// not the previously-declared 'unsure' here, which never matched reality.
export const sessionAvailability = pgTable("session_availability", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").references(() => sessions.id).notNull(),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  status: text("status", { enum: ["yes", "no", "maybe"] }).notNull().default("maybe"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // Declared to match what's already live in production (created via
  // drizzle/0001_wave1_availability_mini_mvp.sql + 0025's ALTER, confirmed
  // against real pg_constraint/pg_indexes — see DOCS/sprint-log.md,
  // 2026-08-17) — not new constraints, code hygiene so a push-based rebuild
  // (e.g. Option C's CI databases) doesn't silently produce a weaker table.
  sessionIdIdx: index("idx_session_availability_session_id").on(table.sessionId),
  parentIdIdx: index("idx_session_availability_parent_id").on(table.parentId),
  kidIdIdx: index("idx_session_availability_kid_id").on(table.kidId),
  statusCheck: check("session_availability_status_check", sql`${table.status} IN ('yes', 'no', 'maybe')`),
}));

// Games — a per-game availability request, distinct from `sessions`
// (recurring weekly practice). Coach pushes a request for a specific
// upcoming game roughly a week ahead; parents respond via `gameAvailability`
// below. Deliberately NOT folded into `sessions`/`sessionAvailability`: a
// game doesn't have a title/ageGroup, and its venue varies per game (a
// free-text name + address), unlike practice's fixed `locations` enum —
// bending `sessions` to fit would mean nullable-everything on a table three
// other features depend on. See DOCS/sprint-log.md, 2026-08-22 entry, for
// the full investigation.
//
// `status` is a minimal scheduled/cancelled column only — no reason field,
// no cancellation notifications. Included now because retrofitting a status
// column later means a second migration for something that costs nothing to
// add up front; the actual cancel workflow (UI, notifications) is a
// separate, later feature pass.
export const games = pgTable("games", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  batchId: integer("batch_id").references(() => batches.id).notNull(),
  opponent: text("opponent"),
  startTime: timestamp("start_time").notNull(),
  venueName: text("venue_name").notNull(),
  venueAddress: text("venue_address"),
  status: text("status", { enum: ["scheduled", "cancelled"] }).notNull().default("scheduled"),
  createdByUserId: integer("created_by_user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Game Availability — parent yes/no/maybe responses for a specific game.
// Same shape and vocabulary as `sessionAvailability` (parentId stored
// alongside kidId for ownership checks and any future reminder plumbing,
// same reason it's on sessionAvailability) — deliberately mirrored, not
// reused, since a game and a session are different entities.
export const gameAvailability = pgTable("game_availability", {
  id: serial("id").primaryKey(),
  gameId: integer("game_id").references(() => games.id).notNull(),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  status: text("status", { enum: ["yes", "no", "maybe"] }).notNull().default("maybe"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  gameIdIdx: index("idx_game_availability_game_id").on(table.gameId),
  parentIdIdx: index("idx_game_availability_parent_id").on(table.parentId),
  kidIdIdx: index("idx_game_availability_kid_id").on(table.kidId),
  statusCheck: check("game_availability_status_check", sql`${table.status} IN ('yes', 'no', 'maybe')`),
}));

// Fitness Records
export const fitnessRecords = pgTable("fitness_records", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  playerId: integer("player_id").references(() => players.id).notNull(),
  recordDate: date("record_date").notNull(),
  runningSpeed: decimal("running_speed", { precision: 5, scale: 2 }),
  endurance: decimal("endurance", { precision: 5, scale: 2 }),
  strength: decimal("strength", { precision: 5, scale: 2 }),
  agility: decimal("agility", { precision: 5, scale: 2 }),
  flexibility: decimal("flexibility", { precision: 5, scale: 2 }),
  // Added PR-FITNESS-1a — match the KidFitnessTab metric set already shipped
  // against this (previously nonexistent) shape.
  weightKg: decimal("weight_kg", { precision: 5, scale: 2 }),
  heightCm: decimal("height_cm", { precision: 5, scale: 2 }),
  bmi: decimal("bmi", { precision: 5, scale: 2 }),
  yoyoLevel: decimal("yoyo_level", { precision: 5, scale: 2 }),
  pushUps: integer("push_ups"),
  shuttleRunSec: decimal("shuttle_run_sec", { precision: 5, scale: 2 }),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Player Assessments — discipline-specific intake/progress/milestone
// evaluations (batting / pace bowling / spin bowling), each at a skill
// level (beginner / intermediate / advanced). Checkpoint-level scores
// live in assessmentScores below. (PR-ASSESS-1a)
export const playerAssessments = pgTable("player_assessments", {
  id: serial("id").primaryKey(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  coachId: integer("coach_id").references(() => users.id).notNull(),
  discipline: text("discipline").notNull(), // 'batting' | 'pace_bowling' | 'spin_bowling'
  skillLevel: text("skill_level").notNull(), // 'beginner' | 'intermediate' | 'advanced'
  assessedAt: timestamp("assessed_at").defaultNow().notNull(),
  status: text("status").notNull().default('intake'), // 'intake' | 'progress_check' | 'milestone'
  notes: text("notes"),
}, (table) => ({
  kidIdIdx: index("idx_player_assessments_kid_id").on(table.kidId),
  academyIdIdx: index("idx_player_assessments_academy_id").on(table.academyId),
}));

// Per-checkpoint scores for a player_assessments row. checkpoint_key is
// free text — the discipline/level → checkpoint list mapping lives in
// client/src/config/assessmentCheckpoints.ts (PR-ASSESS-1b), not the DB.
export const assessmentScores = pgTable("assessment_scores", {
  id: serial("id").primaryKey(),
  assessmentId: integer("assessment_id").references(() => playerAssessments.id).notNull(),
  checkpointKey: text("checkpoint_key").notNull(), // e.g. 'grip', 'line_length', 'run_up'
  score: integer("score").notNull(), // 1-5
  notes: text("notes"),
}, (table) => ({
  assessmentIdIdx: index("idx_assessment_scores_assessment_id").on(table.assessmentId),
}));

// ─── Dallas Hub Selection Pipeline (PR-EVAL-1a) ────────────────────────────
// Schema only — no routes/UI yet. hub_id is a plain integer column (NOT a
// foreign key) because the hubs table doesn't exist yet; it's added once
// PR-HUB-1 lands. composite_score and rank are nullable — both are computed
// later by the selection pipeline, never set at insert time.

// One selection event per age-group per phase, e.g. "Phase 2 Spring 2026 — U13".
export const selectionEvents = pgTable("selection_events", {
  id: serial("id").primaryKey(),
  hubId: integer("hub_id").notNull(), // plain int — no hubs table yet (PR-HUB-1)
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  name: text("name").notNull(),
  ageGroup: text("age_group").notNull(), // 'U11' | 'U13' | 'U15' | 'U17'
  phase: text("phase").notNull(), // 'phase1' | 'phase2' | 'regional' | 'conference'
  eventDate: date("event_date"),
  location: text("location"),
  status: text("status").notNull().default('upcoming'), // 'upcoming' | 'active' | 'completed' | 'cancelled'
  notes: text("notes"),
  createdBy: integer("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  hubIdIdx: index("idx_selection_events_hub_id").on(table.hubId),
  academyIdIdx: index("idx_selection_events_academy_id").on(table.academyId),
}));

// A player entered into a selection event. composite_score / rank are
// computed by the selection pipeline after evaluation_scores are in —
// never set at insert time.
export const selectionCandidates = pgTable("selection_candidates", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").references(() => selectionEvents.id).notNull(),
  playerId: integer("player_id").references(() => players.id).notNull(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  status: text("status").notNull().default('pending'), // 'pending' | 'selected' | 'reserve' | 'not_selected'
  compositeScore: decimal("composite_score", { precision: 3, scale: 2 }),
  rank: integer("rank"),
  notes: text("notes"),
  notifiedAt: timestamp("notified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  eventIdIdx: index("idx_selection_candidates_event_id").on(table.eventId),
  academyIdIdx: index("idx_selection_candidates_academy_id").on(table.academyId),
  uniqEventPlayer: uniqueIndex("selection_candidates_event_player_uniq").on(table.eventId, table.playerId),
}));

// Per-evaluator, per-category score for a candidate. The 4 categories
// (core_skills / game_awareness / mental_toughness / athletic_ability)
// mirror the 23-criterion rubric used by the Hub Selection demo UI.
export const evaluationScores = pgTable("evaluation_scores", {
  id: serial("id").primaryKey(),
  eventId: integer("event_id").references(() => selectionEvents.id).notNull(),
  candidateId: integer("candidate_id").references(() => selectionCandidates.id).notNull(),
  evaluatorId: integer("evaluator_id").references(() => users.id).notNull(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  category: text("category").notNull(), // 'core_skills' | 'game_awareness' | 'mental_toughness' | 'athletic_ability'
  score: decimal("score", { precision: 3, scale: 2 }).notNull(), // 1.0-5.0
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  eventIdIdx: index("idx_evaluation_scores_event_id").on(table.eventId),
  candidateIdIdx: index("idx_evaluation_scores_candidate_id").on(table.candidateId),
  academyIdIdx: index("idx_evaluation_scores_academy_id").on(table.academyId),
  uniqCandidateEvaluatorCategory: uniqueIndex("evaluation_scores_candidate_evaluator_category_uniq").on(
    table.candidateId, table.evaluatorId, table.category
  ),
}));

// Meal Plans
export const mealPlans = pgTable("meal_plans", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  ageGroup: text("age_group").$type<string>().notNull(),
  location: text("location", { enum: locations }),
  title: text("title").notNull(),
  weekStartDate: date("week_start_date").notNull(),
  weekEndDate: date("week_end_date").notNull(),
  createdBy: integer("created_by").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Meal Items
export const mealItems = pgTable("meal_items", {
  id: serial("id").primaryKey(),
  mealPlanId: integer("meal_plan_id").references(() => mealPlans.id).notNull(),
  dayOfWeek: integer("day_of_week").notNull(), // 0 = Sunday, 1 = Monday, etc.
  mealType: text("meal_type").notNull(), // breakfast, lunch, dinner, snack
  items: text("items").array().notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Announcement priorities
export const announcementPriorities = ["info", "warning", "urgent"] as const;
export type AnnouncementPriority = (typeof announcementPriorities)[number];

// Announcements
export const announcements = pgTable("announcements", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  title: text("title").notNull(),
  content: text("content").notNull(),
  message: text("message").notNull(), // Alias for content for consistency
  priority: text("priority", { enum: announcementPriorities }).notNull().default("info"),
  tags: text("tags").array(), // e.g., ["schedule", "fees", "events"]
  effectiveFrom: timestamp("effective_from").defaultNow().notNull(),
  effectiveTo: timestamp("effective_to"), // null means no expiry
  createdBy: integer("created_by").references(() => users.id).notNull(),
  targetAgeGroups: text("target_age_groups").array(), // array of age groups or 'all'
  targetLocations: text("target_locations").array(), // array of locations or 'all'
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Announcement Views (to track if parents have viewed announcements)
export const announcementViews = pgTable("announcement_views", {
  id: serial("id").primaryKey(),
  announcementId: integer("announcement_id").references(() => announcements.id).notNull(),
  userId: integer("user_id").references(() => users.id).notNull(),
  viewedAt: timestamp("viewed_at").defaultNow().notNull(),
});

// Batches table for organizing kids into training groups.
//
// Naming note: parent-facing UI calls these "Teams"; internal/admin terms
// alternate between "Batch" and "Team". The DB stays as `batches` to avoid
// a destructive rename. See client/src/pages/coach/TeamManagement.tsx for
// the parent/admin labelling.
//
// Multi-batch enrollment: `batchEnrollments` below is the many-to-many
// join table for train-up, dual-register, guest-play, tournament-squad,
// and (the case that actually triggered building it) a twice-weekly kid
// split across two single-day batches. `players.batch_id` predates this
// and is NOT yet wired to it — see that column's own comment for the
// still-open question of what it should mean going forward. Do NOT
// hard-code one-player-one-team assumptions anywhere in batch logic.
export const batches = pgTable("batches", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  name: varchar("name", { length: 255 }).notNull(),
  ageGroup: text("age_group").notNull(),
  ageRange: varchar("age_range", { length: 10 }).notNull(),
  // 'Beginner' | 'Intermediate' | 'Advanced'. Text column for migration
  // simplicity; enum enforcement lives in the API layer (server + client)
  // so we can evolve labels without a schema migration.
  skillLevel: text("skill_level"),
  // 'hard' | 'tennis' — lowercase, deliberately a different vocabulary from
  // players.ballType ("Hard/Leather ball" | "Tennis ball"). Normalize both
  // to this shared lowercase form in the mapping layer (shared/batchSuggestion.ts),
  // don't change either column to match the other.
  ballType: text("ball_type"),
  // JSON array of day abbreviations, e.g. '["Mon","Wed"]'. Parse before use.
  days: text("days"),
  timeSlot: text("time_slot"),
  sessionsPerWeek: integer("sessions_per_week"),
  schedule: varchar("schedule", { length: 255 }),
  maxCapacity: integer("max_capacity").default(20),
  // System-managed. NOT editable from the client; derived from player→team
  // assignments once those land in PR-X3. UI displays read-only / "—" until
  // the derivation is wired up.
  currentEnrollment: integer("current_enrollment").default(0),
  coachId: integer("coach_id").references(() => users.id).notNull(),
  venue: varchar("venue", { length: 255 }),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Batch Enrollments — many-to-many join table for a player belonging to
// more than one batch at once (twice-weekly kids split across two
// single-day batches, train-up, dual-register, guest-play, tournament
// squads). Schema only in this PR — no route writes to this table yet;
// POST/DELETE /api/batches/:id/kids/:kidId still overwrites
// players.batch_id, unchanged. `enrolledAt` has no DB-level default —
// the app sets it explicitly on insert (same convention as
// sessionReminders/scheduleNotifications' createdAt), not a SQLite
// ADD COLUMN default which would hit the CURRENT_TIMESTAMP restriction
// documented elsewhere in this project; moot for a CREATE TABLE column
// but kept consistent with that convention anyway.
export const batchEnrollments = pgTable("batch_enrollments", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  playerId: integer("player_id").references(() => players.id).notNull(),
  batchId: integer("batch_id").references(() => batches.id).notNull(),
  enrolledAt: timestamp("enrolled_at").notNull(),
}, (table) => ({
  uniquePlayerBatch: unique().on(table.playerId, table.batchId),
}));

// Training Sessions table (different from existing sessions - these are for batch training)
export const trainingSessions = pgTable("training_sessions", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  batchId: integer("batch_id").references(() => batches.id).notNull(),
  coachId: integer("coach_id").references(() => users.id).notNull(),
  date: date("date").notNull(),
  theme: varchar("theme", { length: 255 }).notNull(),
  weekNumber: integer("week_number"), // 1-4 for curriculum rotation
  sessionType: varchar("session_type", { length: 1 }), // 'A' or 'B'
  defaultSkillRating: integer("default_skill_rating"), // 1-3
  status: varchar("status", { length: 20 }).default("planned"), // planned, active, completed
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Session Logs table for individual kid performance tracking
export const sessionLogs = pgTable("session_logs", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  trainingSessionId: integer("training_session_id").references(() => trainingSessions.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  attendance: boolean("attendance").notNull().default(false),
  skillRating: integer("skill_rating"), // 1-3 stars
  oversBowled: decimal("overs_bowled", { precision: 3, scale: 1 }).default("0"),
  painFlag: boolean("pain_flag").default(false),
  painNotes: text("pain_notes"),
  individualNotes: text("individual_notes"),
  drillTags: text("drill_tags").array(),
  // Batting sub-attributes (1-5, nullable — coach fills what's relevant)
  battingGrip: integer("batting_grip"),
  battingStance: integer("batting_stance"),
  battingFootwork: integer("batting_footwork"),
  battingTransfer: integer("batting_transfer"),
  battingHead: integer("batting_head"),
  // Bowling sub-attributes (1-5, nullable)
  bowlingLoad: integer("bowling_load"),
  bowlingBfc: integer("bowling_bfc"),
  bowlingBrace: integer("bowling_brace"),
  bowlingRelease: integer("bowling_release"),
  // Fielding sub-attributes (1-5, nullable)
  fieldingCatch: integer("fielding_catch"),
  fieldingThrow: integer("fielding_throw"),
  fieldingGround: integer("fielding_ground"),
  // Free-text note per kid per session
  coachNote: text("coach_note"),
  idempotencyKey: varchar("idempotency_key", { length: 255 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // The previous two primaryKey() calls here were invalid (a table can
  // only have one primary key) and would have made `drizzle generate`
  // emit broken SQL — that part needed fixing regardless. But real
  // deployed data (dev.db, confirmed via `sqlite3 dev.db` during
  // chore/migrate-sqlite-to-postgres) rules out UNIQUE as the correct
  // replacement on either column set: (training_session_id, kid_id) has
  // real duplicates up to 42x, and idempotency_key is deliberately
  // reused across every kid-row in one POST /api/sessions/log batch
  // submission (see server/routes/session-logs.ts ~line 213-326 — one
  // idempotencyKey per submission, N rows per submission, one per kid).
  // DOCS/cricket-hub-audit-2026-06-19.md's claim that production's
  // deployed schema already enforces UNIQUE here (via 0003) doesn't hold
  // up against real data — 0003 never actually ran (see sprint log), and
  // the live dev.db table has never enforced either constraint. Plain
  // (non-unique) indexes only, matching real query patterns — no
  // uniqueness invariant actually exists on this table.
  idxTrainingSession: index("idx_session_logs_training_session_id").on(table.trainingSessionId),
  idxIdempotencyKey: index("idx_session_logs_idempotency_key").on(table.idempotencyKey),
}));

// Weekly Overs tracking for kids (safety constraint)
export const weeklyOvers = pgTable("weekly_overs", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  weekStartDate: date("week_start_date").notNull(),
  totalOvers: decimal("total_overs", { precision: 4, scale: 1 }).default("0"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (table) => ({
  // Same bug class as sessionLogs above, found independently while
  // building the fresh-Postgres bootstrap migration (not previously
  // flagged in DOCS/cricket-hub-audit-2026-06-19.md — that audit only
  // covered session_logs): `id` is already a serial primary key, so a
  // second table-level primaryKey() here is invalid (a table can only
  // have one). Fixed to unique() — id stays the real PK, this stays the
  // business-uniqueness constraint it was clearly meant to be.
  uniqueKidWeek: unique().on(table.kidId, table.weekStartDate),
}));

// Homework Assignments table for automated skill improvement
export const homeworkAssignments = pgTable("homework_assignments", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  trainingSessionId: integer("training_session_id").references(() => trainingSessions.id).notNull(),
  skillArea: varchar("skill_area", { length: 100 }).notNull(),
  triggerRating: integer("trigger_rating"), // 1-3
  drillName: varchar("drill_name", { length: 255 }).notNull(),
  description: text("description").notNull(),
  videoUrl: varchar("video_url", { length: 500 }),
  durationMinutes: integer("duration_minutes").notNull(),
  frequency: varchar("frequency", { length: 20 }).default("daily"),
  totalDays: integer("total_days").default(7),
  startDate: date("start_date").notNull(),
  endDate: date("end_date").notNull(),
  status: varchar("status", { length: 20 }).default("assigned"), // assigned, in-progress, completed, skipped
  completionRate: integer("completion_rate").default(0), // 0-100
  parentNotes: text("parent_notes"),
  coachReview: text("coach_review"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Homework Completions table for tracking daily practice
export const homeworkCompletions = pgTable("homework_completions", {
  id: serial("id").primaryKey(),
  homeworkId: integer("homework_id").references(() => homeworkAssignments.id).notNull(),
  completionDate: date("completion_date").notNull(),
  durationMinutes: integer("duration_minutes").notNull(),
  qualityRating: integer("quality_rating"), // 1-5
  notes: text("notes"),
  videoSubmission: varchar("video_submission", { length: 500 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Payments
export const payments = pgTable("payments", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  playerId: integer("player_id").references(() => players.id).notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
  paymentType: text("payment_type").notNull(), // monthly_fee, equipment_fee, tournament_fee, etc.
  sessionDuration: text("session_duration", { enum: sessionDurations }),
  expectedAmount: decimal("expected_amount", { precision: 10, scale: 2 }),
  isOverUnderPayment: boolean("is_over_under_payment").default(false),
  month: text("month"), // Format: YYYY-MM
  dueDate: date("due_date").notNull(),
  paidDate: timestamp("paid_date"),
  status: text("status").notNull().default("pending"), // pending, pending_verification, paid
  paymentMethod: text("payment_method"), // zelle, cash, credit card, venmo, cashapp
  parentNote: text("parent_note"), // note parent adds when clicking "I have paid"
  stripePaymentIntentId: text("stripe_payment_intent_id"), // dormant — Phase 2
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Parent-Child Connection Requests
export const connectionRequests = pgTable("connection_requests", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  playerId: integer("player_id").references(() => players.id).notNull(),
  status: text("status").notNull().default("pending"), // pending, approved, rejected
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Admin Registration Invitations 
export const adminInvitations = pgTable("admin_invitations", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  token: text("token").notNull().unique(),
  email: text("email").notNull(),
  role: text("role", { enum: userRoles }).notNull().default("admin"),
  status: text("status", { enum: invitationStatuses }).notNull().default("pending"),
  createdById: integer("created_by_id").references(() => users.id).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  usedByIp: text("used_by_ip"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Session Reminders - for tracking parent RSVP reminders
export const sessionReminders = pgTable("session_reminders", {
  id: serial("id").primaryKey(),
  sessionId: integer("session_id").references(() => sessions.id).notNull(),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  kidId: integer("kid_id").references(() => players.id), // optional - can be null for general reminders
  channel: text("channel").notNull().default("email"), // email, sms, push, etc.
  status: text("status").notNull().default("queued"), // queued, sent, failed
  sentAt: text("sent_at"), // ISO string - application-layer timestamp
  errorText: text("error_text"), // error details if status is failed
  createdAt: text("created_at").notNull(), // ISO string - application-layer timestamp
  updatedAt: text("updated_at").notNull(), // ISO string - application-layer timestamp
}, (table) => ({
  // Unique constraint for dedupe logic - one reminder per session/parent/channel.
  // Same bug class as sessionLogs/weeklyOvers above (id is already the real
  // primary key, so this can't also be one) — found in the same systematic
  // sweep while building chore/migrate-sqlite-to-postgres. This table has
  // never been deployed to Postgres at all yet, so there's no existing-
  // deployment precedent to match; unique() is simply correct.
  uniqueSessionParentChannel: unique().on(table.sessionId, table.parentId, table.channel),
}));

// Reminder Settings - admin controls for reminder system
export const reminderSettings = pgTable("reminder_settings", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id), // null for global settings
  enabled: boolean("enabled").notNull().default(true),
  sendWindowMinutes: integer("send_window_minutes").notNull().default(120), // 2 hours before session
  dedupeHours: integer("dedupe_hours").notNull().default(6), // hours to wait before sending duplicate
  lastRunAt: text("last_run_at"), // ISO string - application-layer timestamp
  lastRunSummary: text("last_run_summary"), // JSON string with counts
  createdAt: text("created_at").notNull(), // ISO string - application-layer timestamp
  updatedAt: text("updated_at").notNull(), // ISO string - application-layer timestamp
});

// Weekly Digest Sends - tracks one weekly "upcoming practice this week" email
// per parent, distinct from sessionReminders (which is per-session, time-window
// based). weekStart is the digest run's window-start date, not a specific
// session — the unique constraint on (parentId, weekStart) is what makes a
// re-triggered cron run idempotent instead of double-sending.
export const weeklyDigestSends = pgTable("weekly_digest_sends", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  weekStart: date("week_start").notNull(),
  status: text("status", { enum: ["sent", "failed"] }).notNull(),
  sentAt: timestamp("sent_at"),
  errorText: text("error_text"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => ({
  uniqueParentWeek: unique().on(table.parentId, table.weekStart),
}));

// Schedule Notifications - event-driven parent alerts for schedule changes
// (distinct from sessionReminders, which is a time-window RSVP nudge, not a
// change alert). event_type covers session_updated/session_cancelled even
// though nothing wires those triggers yet (PR-S1 owns session update/cancel
// routes, still paused) — the enum exists now so that work won't need a
// second migration. diff_summary is shape-only per CLAUDE.md's PII rule
// (e.g. "time_changed"), never free-text details.
export const scheduleNotifications = pgTable("schedule_notifications", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  eventType: text("event_type", {
    enum: ["session_created", "session_updated", "session_cancelled", "batch_reassigned", "game_created"],
  }).notNull(),
  sessionId: integer("session_id").references(() => sessions.id), // nullable - null for batch_reassigned
  batchId: integer("batch_id").references(() => batches.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  parentId: integer("parent_id").references(() => users.id).notNull(),
  channel: text("channel", { enum: ["email", "in_app", "sms"] }).notNull().default("email"),
  status: text("status", { enum: ["queued", "sent", "failed"] }).notNull().default("queued"),
  diffSummary: text("diff_summary"), // shape-only, e.g. "time_changed" - NEVER free-text details
  sentAt: text("sent_at"), // ISO string - application-layer timestamp
  errorText: text("error_text"),
  createdAt: text("created_at").notNull(), // ISO string - application-layer timestamp
});

// User Action Audit Logs
// User sessions table for persistent sessions
export const userSessions = pgTable("user_sessions", {
  id: serial("id").primaryKey(),
  sessionId: text("session_id").notNull().unique(),
  userId: integer("user_id").references(() => users.id).notNull(),
  isActive: boolean("is_active").default(true),
  tokenVersion: integer("token_version").default(1),
  expiresAt: timestamp("expires_at").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  lastActiveAt: timestamp("last_active_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// Enhanced audit logs with more detailed tracking
export const userAuditLogs = pgTable("user_audit_logs", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  userId: integer("user_id").references(() => users.id),
  // Removing reference to userSessions.sessionId to fix the error
  sessionId: text("session_id"),
  action: text("action").notNull(), // login, logout, register, update, delete, approve, reject, etc.
  details: text("details"),
  targetUserId: integer("target_user_id").references(() => users.id),
  targetResourceId: integer("target_resource_id"),
  targetResourceType: text("target_resource_type"),
  apiEndpoint: text("api_endpoint"),
  statusCode: integer("status_code"),
  requestMethod: text("request_method"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  extraData: json("extra_data"),
  createdAt: timestamp("timestamp").defaultNow().notNull(),
});

// Define relationships
export const academiesRelations = relations(academies, ({ many }) => ({
  users: many(users),
  players: many(players),
  sessions: many(sessions),
  mealPlans: many(mealPlans),
  announcements: many(announcements),
  payments: many(payments),
  connectionRequests: many(connectionRequests),
  adminInvitations: many(adminInvitations),
  auditLogs: many(userAuditLogs),
  batches: many(batches),
  trainingSessions: many(trainingSessions),
  sessionLogs: many(sessionLogs),
  weeklyOvers: many(weeklyOvers),
  homeworkAssignments: many(homeworkAssignments),
}));

export const usersRelations = relations(users, ({ many, one }) => ({
  academy: one(academies, { fields: [users.academyId], references: [academies.id] }),
  players: many(players),
  sessions: many(sessions),
  userSessions: many(userSessions),
  announcementViews: many(announcementViews),
  announcements: many(announcements, { relationName: "created_by" }),
  mealPlans: many(mealPlans, { relationName: "created_by" }),
  connectionRequests: many(connectionRequests),
  invitationsCreated: many(adminInvitations, { relationName: "created_by" }),
  auditLogs: many(userAuditLogs),
  batches: many(batches),
  trainingSessions: many(trainingSessions),
}));

export const playersRelations = relations(players, ({ one, many }) => ({
  academy: one(academies, { fields: [players.academyId], references: [academies.id] }),
  parent: one(users, { fields: [players.parentId], references: [users.id] }),
  batch: one(batches, { fields: [players.batchId], references: [batches.id] }),
  fitnessRecords: many(fitnessRecords),
  assessments: many(playerAssessments),
  selectionCandidacies: many(selectionCandidates),
  payments: many(payments),
  sessionAttendances: many(sessionAttendances),
  connectionRequests: many(connectionRequests),
  sessionLogs: many(sessionLogs),
  weeklyOvers: many(weeklyOvers),
  homeworkAssignments: many(homeworkAssignments),
  batchEnrollments: many(batchEnrollments),
}));

export const sessionsRelations = relations(sessions, ({ one, many }) => ({
  academy: one(academies, { fields: [sessions.academyId], references: [academies.id] }),
  coach: one(users, { fields: [sessions.coachId], references: [users.id] }),
  attendances: many(sessionAttendances),
}));

export const batchesRelations = relations(batches, ({ one, many }) => ({
  academy: one(academies, { fields: [batches.academyId], references: [academies.id] }),
  coach: one(users, { fields: [batches.coachId], references: [users.id] }),
  trainingSessions: many(trainingSessions),
  enrollments: many(batchEnrollments),
}));

export const batchEnrollmentsRelations = relations(batchEnrollments, ({ one }) => ({
  academy: one(academies, { fields: [batchEnrollments.academyId], references: [academies.id] }),
  player: one(players, { fields: [batchEnrollments.playerId], references: [players.id] }),
  batch: one(batches, { fields: [batchEnrollments.batchId], references: [batches.id] }),
}));

export const trainingSessionsRelations = relations(trainingSessions, ({ one, many }) => ({
  academy: one(academies, { fields: [trainingSessions.academyId], references: [academies.id] }),
  batch: one(batches, { fields: [trainingSessions.batchId], references: [batches.id] }),
  coach: one(users, { fields: [trainingSessions.coachId], references: [users.id] }),
  sessionLogs: many(sessionLogs),
  homeworkAssignments: many(homeworkAssignments),
}));

export const sessionLogsRelations = relations(sessionLogs, ({ one }) => ({
  academy: one(academies, { fields: [sessionLogs.academyId], references: [academies.id] }),
  trainingSession: one(trainingSessions, { fields: [sessionLogs.trainingSessionId], references: [trainingSessions.id] }),
  kid: one(players, { fields: [sessionLogs.kidId], references: [players.id] }),
}));

export const weeklyOversRelations = relations(weeklyOvers, ({ one }) => ({
  academy: one(academies, { fields: [weeklyOvers.academyId], references: [academies.id] }),
  kid: one(players, { fields: [weeklyOvers.kidId], references: [players.id] }),
}));

export const homeworkAssignmentsRelations = relations(homeworkAssignments, ({ one, many }) => ({
  academy: one(academies, { fields: [homeworkAssignments.academyId], references: [academies.id] }),
  kid: one(players, { fields: [homeworkAssignments.kidId], references: [players.id] }),
  trainingSession: one(trainingSessions, { fields: [homeworkAssignments.trainingSessionId], references: [trainingSessions.id] }),
  completions: many(homeworkCompletions),
}));

export const homeworkCompletionsRelations = relations(homeworkCompletions, ({ one }) => ({
  homework: one(homeworkAssignments, { fields: [homeworkCompletions.homeworkId], references: [homeworkAssignments.id] }),
}));

export const sessionAttendancesRelations = relations(sessionAttendances, ({ one }) => ({
  session: one(sessions, { fields: [sessionAttendances.sessionId], references: [sessions.id] }),
  player: one(players, { fields: [sessionAttendances.playerId], references: [players.id] }),
  academy: one(academies, { fields: [sessionAttendances.academyId], references: [academies.id] }),
}));

export const sessionAvailabilityRelations = relations(sessionAvailability, ({ one }) => ({
  session: one(sessions, { fields: [sessionAvailability.sessionId], references: [sessions.id] }),
  parent: one(users, { fields: [sessionAvailability.parentId], references: [users.id] }),
  kid: one(players, { fields: [sessionAvailability.kidId], references: [players.id] }),
}));

export const gamesRelations = relations(games, ({ one, many }) => ({
  academy: one(academies, { fields: [games.academyId], references: [academies.id] }),
  batch: one(batches, { fields: [games.batchId], references: [batches.id] }),
  createdByUser: one(users, { fields: [games.createdByUserId], references: [users.id] }),
  responses: many(gameAvailability),
}));

export const gameAvailabilityRelations = relations(gameAvailability, ({ one }) => ({
  game: one(games, { fields: [gameAvailability.gameId], references: [games.id] }),
  parent: one(users, { fields: [gameAvailability.parentId], references: [users.id] }),
  kid: one(players, { fields: [gameAvailability.kidId], references: [players.id] }),
}));

export const fitnessRecordsRelations = relations(fitnessRecords, ({ one }) => ({
  player: one(players, { fields: [fitnessRecords.playerId], references: [players.id] }),
  academy: one(academies, { fields: [fitnessRecords.academyId], references: [academies.id] }),
}));

export const playerAssessmentsRelations = relations(playerAssessments, ({ one, many }) => ({
  kid: one(players, { fields: [playerAssessments.kidId], references: [players.id] }),
  academy: one(academies, { fields: [playerAssessments.academyId], references: [academies.id] }),
  coach: one(users, { fields: [playerAssessments.coachId], references: [users.id] }),
  scores: many(assessmentScores),
}));

export const assessmentScoresRelations = relations(assessmentScores, ({ one }) => ({
  assessment: one(playerAssessments, { fields: [assessmentScores.assessmentId], references: [playerAssessments.id] }),
}));

export const selectionEventsRelations = relations(selectionEvents, ({ one, many }) => ({
  academy: one(academies, { fields: [selectionEvents.academyId], references: [academies.id] }),
  createdByUser: one(users, { fields: [selectionEvents.createdBy], references: [users.id] }),
  candidates: many(selectionCandidates),
  evaluationScores: many(evaluationScores),
}));

export const selectionCandidatesRelations = relations(selectionCandidates, ({ one, many }) => ({
  event: one(selectionEvents, { fields: [selectionCandidates.eventId], references: [selectionEvents.id] }),
  player: one(players, { fields: [selectionCandidates.playerId], references: [players.id] }),
  academy: one(academies, { fields: [selectionCandidates.academyId], references: [academies.id] }),
  evaluationScores: many(evaluationScores),
}));

export const evaluationScoresRelations = relations(evaluationScores, ({ one }) => ({
  event: one(selectionEvents, { fields: [evaluationScores.eventId], references: [selectionEvents.id] }),
  candidate: one(selectionCandidates, { fields: [evaluationScores.candidateId], references: [selectionCandidates.id] }),
  evaluator: one(users, { fields: [evaluationScores.evaluatorId], references: [users.id] }),
  academy: one(academies, { fields: [evaluationScores.academyId], references: [academies.id] }),
}));

export const mealPlansRelations = relations(mealPlans, ({ one, many }) => ({
  academy: one(academies, { fields: [mealPlans.academyId], references: [academies.id] }),
  createdByUser: one(users, { fields: [mealPlans.createdBy], references: [users.id] }),
  mealItems: many(mealItems),
}));

export const mealItemsRelations = relations(mealItems, ({ one }) => ({
  mealPlan: one(mealPlans, { fields: [mealItems.mealPlanId], references: [mealPlans.id] }),
}));

export const announcementsRelations = relations(announcements, ({ one, many }) => ({
  academy: one(academies, { fields: [announcements.academyId], references: [academies.id] }),
  createdByUser: one(users, { fields: [announcements.createdBy], references: [users.id] }),
  views: many(announcementViews),
}));

export const announcementViewsRelations = relations(announcementViews, ({ one }) => ({
  announcement: one(announcements, { fields: [announcementViews.announcementId], references: [announcements.id] }),
  user: one(users, { fields: [announcementViews.userId], references: [users.id] }),
}));

export const paymentsRelations = relations(payments, ({ one }) => ({
  academy: one(academies, { fields: [payments.academyId], references: [academies.id] }),
  player: one(players, { fields: [payments.playerId], references: [players.id] }),
}));

export const connectionRequestsRelations = relations(connectionRequests, ({ one }) => ({
  academy: one(academies, { fields: [connectionRequests.academyId], references: [academies.id] }),
  parent: one(users, { fields: [connectionRequests.parentId], references: [users.id] }),
  player: one(players, { fields: [connectionRequests.playerId], references: [players.id] }),
}));

export const adminInvitationsRelations = relations(adminInvitations, ({ one }) => ({
  academy: one(academies, { fields: [adminInvitations.academyId], references: [academies.id] }),
  createdBy: one(users, { 
    fields: [adminInvitations.createdById], 
    references: [users.id],
    relationName: "created_by"
  }),
}));

export const sessionRemindersRelations = relations(sessionReminders, ({ one }) => ({
  session: one(sessions, { fields: [sessionReminders.sessionId], references: [sessions.id] }),
  parent: one(users, { fields: [sessionReminders.parentId], references: [users.id] }),
  kid: one(players, { fields: [sessionReminders.kidId], references: [players.id] }),
}));

export const reminderSettingsRelations = relations(reminderSettings, ({ one }) => ({
  academy: one(academies, { fields: [reminderSettings.academyId], references: [academies.id] }),
}));

export const weeklyDigestSendsRelations = relations(weeklyDigestSends, ({ one }) => ({
  academy: one(academies, { fields: [weeklyDigestSends.academyId], references: [academies.id] }),
  parent: one(users, { fields: [weeklyDigestSends.parentId], references: [users.id] }),
}));

export const userSessionsRelations = relations(userSessions, ({ one, many }) => ({
  user: one(users, { fields: [userSessions.userId], references: [users.id] }),
  auditLogs: many(userAuditLogs)
}));

export const userAuditLogsRelations = relations(userAuditLogs, ({ one }) => ({
  academy: one(academies, { fields: [userAuditLogs.academyId], references: [academies.id] }),
  user: one(users, { fields: [userAuditLogs.userId], references: [users.id] }),
  session: one(userSessions, { fields: [userAuditLogs.sessionId], references: [userSessions.sessionId] }),
}));

// Zod Schemas for validation
export const insertUserSchema = createInsertSchema(users, {
  username: (schema) => schema.min(3, "Username must be at least 3 characters"),
  password: (schema) => schema.min(6, "Password must be at least 6 characters"),
  email: (schema) => schema.email("Please enter a valid email address"),
  fullName: (schema) => schema.min(2, "Full name must be at least 2 characters"),
  role: (schema) => z.enum(userRoles, {
    errorMap: () => ({ message: "Please select a valid role" }),
  }),
  phone: (schema) => schema.optional().refine(val => !val || val.length >= 10, 
    "Phone number must be at least 10 digits")
});
export const insertPlayerSchema = createInsertSchema(players, {
  // Override dateOfBirth to accept both Date objects and ISO string dates
  dateOfBirth: (schema) => z.union([
    z.date(),
    z.string().refine((val) => !isNaN(new Date(val).getTime()), {
      message: "Invalid date string format, must be ISO 8601"
    }).transform(val => new Date(val))
  ]),
});
export const insertSessionSchema = createInsertSchema(sessions);
export const insertFitnessRecordSchema = createInsertSchema(fitnessRecords);
export const insertPlayerAssessmentSchema = createInsertSchema(playerAssessments);
export const insertAssessmentScoreSchema = createInsertSchema(assessmentScores);
export const insertSelectionEventSchema = createInsertSchema(selectionEvents);
export const insertSelectionCandidateSchema = createInsertSchema(selectionCandidates);
export const insertEvaluationScoreSchema = createInsertSchema(evaluationScores);
export const insertMealPlanSchema = createInsertSchema(mealPlans);
export const insertMealItemSchema = createInsertSchema(mealItems);
export const insertAnnouncementSchema = createInsertSchema(announcements);
export const insertPaymentSchema = createInsertSchema(payments);
export const insertConnectionRequestSchema = createInsertSchema(connectionRequests);
export const insertSessionReminderSchema = createInsertSchema(sessionReminders);
export const insertReminderSettingsSchema = createInsertSchema(reminderSettings);
export const insertWeeklyDigestSendSchema = createInsertSchema(weeklyDigestSends);
export const insertUserSessionSchema = createInsertSchema(userSessions);
export const insertUserAuditLogSchema = createInsertSchema(userAuditLogs);
export const insertSessionAvailabilitySchema = createInsertSchema(sessionAvailability);
export const insertGameSchema = createInsertSchema(games);
export const insertGameAvailabilitySchema = createInsertSchema(gameAvailability);
export const insertBatchSchema = createInsertSchema(batches);
export const insertBatchEnrollmentSchema = createInsertSchema(batchEnrollments);
export const insertTrainingSessionSchema = createInsertSchema(trainingSessions);
export const insertSessionLogSchema = createInsertSchema(sessionLogs);
export const insertWeeklyOversSchema = createInsertSchema(weeklyOvers);
export const insertHomeworkAssignmentSchema = createInsertSchema(homeworkAssignments);
export const insertHomeworkCompletionSchema = createInsertSchema(homeworkCompletions);

// Create select schemas
export const sessionReminderSchema = createSelectSchema(sessionReminders);
export const reminderSettingsSchema = createSelectSchema(reminderSettings);
export const weeklyDigestSendSchema = createSelectSchema(weeklyDigestSends);
export const userSchema = createSelectSchema(users);
export const sessionAvailabilitySchema = createSelectSchema(sessionAvailability);
export const gameSchema = createSelectSchema(games);
export const gameAvailabilitySchema = createSelectSchema(gameAvailability);
export const playerSchema = createSelectSchema(players);
export const sessionSchema = createSelectSchema(sessions);
export const fitnessRecordSchema = createSelectSchema(fitnessRecords);
export const playerAssessmentSchema = createSelectSchema(playerAssessments);
export const assessmentScoreSchema = createSelectSchema(assessmentScores);
export const selectionEventSchema = createSelectSchema(selectionEvents);
export const selectionCandidateSchema = createSelectSchema(selectionCandidates);
export const evaluationScoreSchema = createSelectSchema(evaluationScores);
export const mealPlanSchema = createSelectSchema(mealPlans);
export const mealItemSchema = createSelectSchema(mealItems);
export const announcementSchema = createSelectSchema(announcements);
export const paymentSchema = createSelectSchema(payments);
export const connectionRequestSchema = createSelectSchema(connectionRequests);
export const userSessionSchema = createSelectSchema(userSessions);
export const userAuditLogSchema = createSelectSchema(userAuditLogs);
export const batchSchema = createSelectSchema(batches);
export const batchEnrollmentSchema = createSelectSchema(batchEnrollments);
export const trainingSessionSchema = createSelectSchema(trainingSessions);
export const sessionLogSchema = createSelectSchema(sessionLogs);
export const weeklyOversSchema = createSelectSchema(weeklyOvers);
export const homeworkAssignmentSchema = createSelectSchema(homeworkAssignments);
export const homeworkCompletionSchema = createSelectSchema(homeworkCompletions);

// Types for the schemas
export type InsertSessionReminder = z.infer<typeof insertSessionReminderSchema>;
export type SessionReminder = z.infer<typeof sessionReminderSchema>;

export type InsertReminderSettings = z.infer<typeof insertReminderSettingsSchema>;
export type ReminderSettings = z.infer<typeof reminderSettingsSchema>;

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = z.infer<typeof userSchema>;

export type InsertPlayer = z.infer<typeof insertPlayerSchema>;
export type Player = z.infer<typeof playerSchema>;

export type InsertSession = z.infer<typeof insertSessionSchema>;
export type Session = z.infer<typeof sessionSchema>;

export type InsertFitnessRecord = z.infer<typeof insertFitnessRecordSchema>;
export type FitnessRecord = z.infer<typeof fitnessRecordSchema>;
export type InsertPlayerAssessment = z.infer<typeof insertPlayerAssessmentSchema>;
export type PlayerAssessment = z.infer<typeof playerAssessmentSchema>;
export type InsertAssessmentScore = z.infer<typeof insertAssessmentScoreSchema>;
export type AssessmentScore = z.infer<typeof assessmentScoreSchema>;

export type InsertSelectionEvent = z.infer<typeof insertSelectionEventSchema>;
export type SelectionEvent = z.infer<typeof selectionEventSchema>;
export type InsertSelectionCandidate = z.infer<typeof insertSelectionCandidateSchema>;
export type SelectionCandidate = z.infer<typeof selectionCandidateSchema>;
export type InsertEvaluationScore = z.infer<typeof insertEvaluationScoreSchema>;
export type EvaluationScore = z.infer<typeof evaluationScoreSchema>;

export type InsertMealPlan = z.infer<typeof insertMealPlanSchema>;
export type MealPlan = z.infer<typeof mealPlanSchema>;

export type InsertMealItem = z.infer<typeof insertMealItemSchema>;
export type MealItem = z.infer<typeof mealItemSchema>;

export type InsertAnnouncement = z.infer<typeof insertAnnouncementSchema>;
export type Announcement = z.infer<typeof announcementSchema>;

export type InsertPayment = z.infer<typeof insertPaymentSchema>;
export type Payment = z.infer<typeof paymentSchema>;

export type InsertConnectionRequest = z.infer<typeof insertConnectionRequestSchema>;
export type ConnectionRequest = z.infer<typeof connectionRequestSchema>;

export type InsertUserSession = z.infer<typeof insertUserSessionSchema>;
export type UserSession = z.infer<typeof userSessionSchema>;

export type InsertUserAuditLog = z.infer<typeof insertUserAuditLogSchema>;
export type UserAuditLog = z.infer<typeof userAuditLogSchema>;

export type InsertSessionAvailability = z.infer<typeof insertSessionAvailabilitySchema>;
export type SessionAvailability = z.infer<typeof sessionAvailabilitySchema>;

export type InsertGame = z.infer<typeof insertGameSchema>;
export type Game = z.infer<typeof gameSchema>;

export type InsertGameAvailability = z.infer<typeof insertGameAvailabilitySchema>;
export type GameAvailability = z.infer<typeof gameAvailabilitySchema>;

export type InsertBatch = z.infer<typeof insertBatchSchema>;
export type Batch = z.infer<typeof batchSchema>;

export type InsertBatchEnrollment = z.infer<typeof insertBatchEnrollmentSchema>;
export type BatchEnrollment = z.infer<typeof batchEnrollmentSchema>;

export type InsertWeeklyDigestSend = z.infer<typeof insertWeeklyDigestSendSchema>;
export type WeeklyDigestSend = z.infer<typeof weeklyDigestSendSchema>;

export type InsertTrainingSession = z.infer<typeof insertTrainingSessionSchema>;
export type TrainingSession = z.infer<typeof trainingSessionSchema>;

export type InsertSessionLog = z.infer<typeof insertSessionLogSchema>;
export type SessionLog = z.infer<typeof sessionLogSchema>;

export type InsertWeeklyOvers = z.infer<typeof insertWeeklyOversSchema>;
export type WeeklyOvers = z.infer<typeof weeklyOversSchema>;

export type InsertHomeworkAssignment = z.infer<typeof insertHomeworkAssignmentSchema>;
export type HomeworkAssignment = z.infer<typeof homeworkAssignmentSchema>;

export type InsertHomeworkCompletion = z.infer<typeof insertHomeworkCompletionSchema>;
export type HomeworkCompletion = z.infer<typeof homeworkCompletionSchema>;

// ============================================================================
// AGENT INFRASTRUCTURE — added in PR-1 (feat/agents-pr1-tables, May 2026)
// ----------------------------------------------------------------------------
// Three operational tables (agent_tasks, agent_memory, agent_activity_log)
// plus four domain tables that agent flows read/write through the standard
// approval boundary (admin_action_queue, kid_progress_summary, coach_kid_notes,
// parent_messages). See docs/agents.md for the contract that governs adding
// future agent tables.
//
// Conventions enforced here:
//   - Every tenant-scoped table carries `academyId` to support multi-academy
//     deployments. Tools layer (PR-2) filters every query by academyId.
//   - JSON payloads use Postgres `json()`. The SQLite mirror uses `text()`
//     and the app stringifies/parses via a helper to keep call sites uniform.
//   - Indexes are defined inline via the second-arg callback. Per the
//     standing performance-review rule (AGENT_ARCHITECTURE.md §6.4), every
//     filter/sort column on a dashboard or scoped-read path must be indexed.
// ============================================================================

// Per-agent task record. One row per discrete agent unit of work.
// Lifecycle: queued → running → done | failed | awaiting_approval.
export const agentTasks = pgTable("agent_tasks", {
  id: serial("id").primaryKey(),
  agentName: text("agent_name").notNull(),                 // 'parent_support' | 'coach_ops' | ...
  status: text("status").notNull().default("queued"),      // 'queued'|'running'|'done'|'failed'|'awaiting_approval'
  trigger: text("trigger").notNull(),                      // 'parent_message'|'cron'|'admin_request'|...
  inputSummary: text("input_summary"),
  outputSummary: text("output_summary"),
  relatedUserId: integer("related_user_id").references(() => users.id),
  relatedKidId: integer("related_kid_id").references(() => players.id),
  academyId: integer("academy_id").references(() => academies.id),
  approvalRequired: boolean("approval_required").default(false).notNull(),
  approvalStatus: text("approval_status"),                 // null | 'pending' | 'approved' | 'rejected'
  approverUserId: integer("approver_user_id").references(() => users.id),
  tokenCost: integer("token_cost").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
}, (t) => ({
  // Dashboard "open work for academy X" — most common admin view.
  idxAgentTasksAcademyStatus: index("agent_tasks_academy_status_idx").on(t.academyId, t.status),
  // Per-agent dashboard ("show me everything Parent Support did this week").
  idxAgentTasksAcademyAgent: index("agent_tasks_academy_agent_idx").on(t.academyId, t.agentName),
  // "Tasks that touched this user" — parent profile audit trail.
  idxAgentTasksRelatedUser: index("agent_tasks_related_user_idx").on(t.relatedUserId),
  // "Tasks that touched this kid" — kid profile audit trail.
  idxAgentTasksRelatedKid: index("agent_tasks_related_kid_idx").on(t.relatedKidId),
  // Chronological listing without an academy filter (rare; cheap idx).
  idxAgentTasksCreated: index("agent_tasks_created_idx").on(t.createdAt),
}));

// Per-agent, per-user (and optionally per-kid) working memory.
// UNIQUE-per-slot upsert model: one row per (agent, user, kid, key) tuple.
// Conversation history is NOT stored here — that lives in parent_messages.
// This table holds preferences, style fingerprints, last-context snapshots.
//
// SECURITY (data classification = TENANT, never RESTRICTED):
//   PROHIBITED in `value`: full payment instruments, Stripe / processor IDs,
//   medical conditions, allergy lists, raw parent message bodies, raw email
//   bodies, OAuth tokens, session cookies, passwords, API keys.
//   PERMITTED in `value`: parent UI preferences, language preference, agent
//   tone/style fingerprints, summarized last-context (not raw transcripts),
//   anonymized counters.
//   Enforcement is via the write helper (PR-2: server/agents/lib/memory.ts),
//   which validates `key` against an allowlist and rejects values that
//   contain redaction-class fields. Direct INSERT/UPDATE without that helper
//   is a review failure.
export const agentMemory = pgTable("agent_memory", {
  id: serial("id").primaryKey(),
  agentName: text("agent_name").notNull(),
  scopeUserId: integer("scope_user_id").references(() => users.id),
  scopeKidId: integer("scope_kid_id").references(() => players.id),
  key: text("key").notNull(),                              // 'preferences' | 'style' | 'last_context' | ...
  value: json("value").notNull(),                          // jsonb in PG; SANITIZED via memory.ts (PR-2).
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // One row per logical slot. NULL columns are part of the key in Postgres
  // (NULLs are not equal), which means a null user_id + null kid_id row is
  // its own slot — that's the desired behaviour for academy-wide memory.
  uniqAgentMemorySlot: uniqueIndex("agent_memory_slot_uniq").on(
    t.agentName,
    t.scopeUserId,
    t.scopeKidId,
    t.key,
  ),
}));

// Append-only audit trail of every step inside an agent task: tool calls,
// model calls, mutations. Drives the admin observability dashboard and the
// per-task replay UI. Partition-friendly via (task_id, created_at) — when
// this grows, partition by month.
//
// `academy_id` is denormalized here (also reachable via task_id → agent_tasks)
// for two reasons: (a) defense-in-depth — admin queries filter by academy
// directly without joining; (b) the (academy_id, task_id, created_at) index
// supports an index-only scan of the admin observability dashboard's primary
// filter, which is its highest-frequency read path.
//
// SECURITY: The `input` and `output` JSON columns must NEVER receive raw
// secrets, tokens, cookies, request headers, full Stripe payloads, or full
// payment instruments. PR-2 introduces server/agents/lib/sanitize.ts as the
// only sanctioned write path. Direct inserts in PR-2+ code MUST go through
// the sanitizer or fail review.
export const agentActivityLog = pgTable("agent_activity_log", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id),
  taskId: integer("task_id").references(() => agentTasks.id).notNull(),
  stepType: text("step_type").notNull(),                   // 'router'|'tool_call'|'model_call'|'mutation'
  stepName: text("step_name").notNull(),                   // e.g. 'read_schedule' or 'parent_support.respond'
  input: json("input"),                                    // SANITIZED only — see sanitize.ts (PR-2)
  output: json("output"),                                  // SANITIZED only — see sanitize.ts (PR-2)
  latencyMs: integer("latency_ms"),
  tokenCost: integer("token_cost").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Replay-by-task — chronological per-task drill-down.
  idxActivityTaskCreated: index("agent_activity_task_created_idx").on(t.taskId, t.createdAt),
  // Admin observability dashboard's primary filter: academy + chronological,
  // optionally drilling down to a single task. Three-column composite chosen
  // so the dashboard's WHERE academy_id = ? AND task_id = ? AND created_at >= ?
  // queries are fully covered.
  idxActivityAcademyTaskCreated: index("agent_activity_academy_task_created_idx").on(
    t.academyId,
    t.taskId,
    t.createdAt,
  ),
}));

// Generic admin action queue. Replaces the originally-proposed
// payment_followups table — kind is a string-with-enum so adding new kinds
// (medical_escalation, schedule_conflict, agent_review, etc.) doesn't
// require a migration. The medical-escalation safety rule lands rows here.
export const adminActionQueueKinds = [
  "payment_followup",
  "medical_escalation",
  "schedule_conflict",
  "agent_review",
] as const;
export type AdminActionQueueKind = (typeof adminActionQueueKinds)[number];

export const adminActionQueueSeverities = ["low", "normal", "high", "urgent"] as const;
export type AdminActionQueueSeverity = (typeof adminActionQueueSeverities)[number];

export const adminActionQueueStatuses = ["open", "in_progress", "resolved", "dismissed"] as const;
export type AdminActionQueueStatus = (typeof adminActionQueueStatuses)[number];

export const agentScheduleCadences = ["daily", "weekly_mon", "weekly_fri", "weekly_sun"] as const;

export const agentSchedules = pgTable("agent_schedules", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  batchId: integer("batch_id").references(() => batches.id),
  jobType: text("job_type").notNull().default("batch_progress_summary"),
  cadence: text("cadence", { enum: agentScheduleCadences }).notNull(),
  enabled: boolean("enabled").notNull().default(true),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at").notNull(),
  createdByUserId: integer("created_by_user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  idxSchedulesAcademy: index("agent_schedules_academy_idx").on(t.academyId, t.enabled),
}));

export const adminActionQueue = pgTable("admin_action_queue", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  kind: text("kind", { enum: adminActionQueueKinds }).notNull(),
  severity: text("severity", { enum: adminActionQueueSeverities }).notNull().default("normal"),
  relatedUserId: integer("related_user_id").references(() => users.id),
  relatedKidId: integer("related_kid_id").references(() => players.id),
  relatedPaymentId: integer("related_payment_id").references(() => payments.id),
  relatedSessionId: integer("related_session_id").references(() => sessions.id),
  agentTaskId: integer("agent_task_id").references(() => agentTasks.id),
  title: text("title").notNull(),
  body: text("body"),
  dueBy: timestamp("due_by"),
  status: text("status", { enum: adminActionQueueStatuses }).notNull().default("open"),
  resolvedByUserId: integer("resolved_by_user_id").references(() => users.id),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // "Open queue for academy" — primary admin landing query.
  idxQueueAcademyStatus: index("admin_queue_academy_status_idx").on(t.academyId, t.status),
  // "All medical escalations / payment followups in academy" — kind filter.
  idxQueueAcademyKind: index("admin_queue_academy_kind_idx").on(t.academyId, t.kind),
  // "What's urgent" — severity-sorted views.
  idxQueueAcademySeverity: index("admin_queue_academy_severity_idx").on(t.academyId, t.severity),
  // "Overdue items" — due_by cursor sort. Plain idx; partial index can come later.
  idxQueueDueBy: index("admin_queue_due_by_idx").on(t.dueBy),
}));

// Daily / weekly rollups of skill ratings and attendance per kid.
// Computed by the Performance Aggregator service (PR-2 / Phase 2),
// not by an LLM. UNIQUE (kid_id, period_start, period_end) prevents
// duplicate aggregation runs.
export const kidProgressSummary = pgTable("kid_progress_summary", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  periodStart: date("period_start").notNull(),
  periodEnd: date("period_end").notNull(),
  ratingsJson: json("ratings_json"),                       // { batting: 7.2, bowling: 6.5, ... }
  attendancePct: integer("attendance_pct"),                // 0-100
  sessionsAttended: integer("sessions_attended").default(0).notNull(),
  sessionsTotal: integer("sessions_total").default(0).notNull(),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // Idempotent aggregation key — re-running an aggregator updates in place.
  uniqKidPeriod: uniqueIndex("kid_progress_period_uniq").on(t.kidId, t.periodStart, t.periodEnd),
  // "All summaries for academy" — admin progress dashboards.
  idxProgressAcademyKid: index("kid_progress_academy_kid_idx").on(t.academyId, t.kidId),
}));

// Coach freeform notes per kid. Distinct from skill ratings (which live in
// the kid_metrics tables) — these are qualitative observations, behavioural
// notes, and parent-facing sharing decisions.
export const coachNoteVisibilities = ["coach_only", "admin", "shared_with_parent"] as const;
export type CoachKidNoteVisibility = (typeof coachNoteVisibilities)[number];

export const coachKidNotes = pgTable("coach_kid_notes", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  coachUserId: integer("coach_user_id").references(() => users.id).notNull(),
  kidId: integer("kid_id").references(() => players.id).notNull(),
  body: text("body").notNull(),
  visibility: text("visibility", { enum: coachNoteVisibilities }).notNull().default("coach_only"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => ({
  // "All my notes (this coach), newest first" — coach dashboard.
  idxCoachKidNotesAcademyCoachCreated: index("coach_kid_notes_academy_coach_created_idx").on(
    t.academyId,
    t.coachUserId,
    t.createdAt,
  ),
  // "All notes for this kid" — kid profile timeline.
  idxCoachKidNotesKid: index("coach_kid_notes_kid_idx").on(t.kidId),
}));

// Inbound + outbound parent communications. Phase 1 only writes web_chat
// rows; SMS/email channels light up in later phases. The resolution_status
// + resolved_by_* columns let the admin dashboard track deflection rate
// (agent-resolved vs human-resolved).
export const parentMessageChannels = ["web_chat", "sms", "email"] as const;
export type ParentMessageChannel = (typeof parentMessageChannels)[number];

export const parentMessageDirections = ["inbound", "outbound"] as const;
export type ParentMessageDirection = (typeof parentMessageDirections)[number];

export const parentMessageResolutionStatuses = [
  "open",
  "agent_replied",
  "human_replied",
  "closed",
] as const;
export type ParentMessageResolutionStatus =
  (typeof parentMessageResolutionStatuses)[number];

export const parentMessages = pgTable("parent_messages", {
  id: serial("id").primaryKey(),
  academyId: integer("academy_id").references(() => academies.id).notNull(),
  parentUserId: integer("parent_user_id").references(() => users.id).notNull(),
  kidId: integer("kid_id").references(() => players.id),
  channel: text("channel", { enum: parentMessageChannels }).notNull().default("web_chat"),
  direction: text("direction", { enum: parentMessageDirections }).notNull(),
  body: text("body").notNull(),
  resolvedByAgentTaskId: integer("resolved_by_agent_task_id").references(() => agentTasks.id),
  resolvedByUserId: integer("resolved_by_user_id").references(() => users.id),
  resolutionStatus: text("resolution_status", { enum: parentMessageResolutionStatuses })
    .notNull()
    .default("open"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => ({
  // Conversation thread for a parent — most-recent-first list.
  idxParentMsgsAcademyParentCreated: index("parent_messages_academy_parent_created_idx").on(
    t.academyId,
    t.parentUserId,
    t.createdAt,
  ),
  // "All messages mentioning this kid" — kid profile timeline.
  idxParentMsgsKid: index("parent_messages_kid_idx").on(t.kidId),
}));

// Relations — match the existing `*Relations` pattern further up the file.
export const agentTasksRelations = relations(agentTasks, ({ one, many }) => ({
  academy: one(academies, { fields: [agentTasks.academyId], references: [academies.id] }),
  relatedUser: one(users, { fields: [agentTasks.relatedUserId], references: [users.id], relationName: "agent_task_related_user" }),
  relatedKid: one(players, { fields: [agentTasks.relatedKidId], references: [players.id] }),
  approver: one(users, { fields: [agentTasks.approverUserId], references: [users.id], relationName: "agent_task_approver" }),
  activity: many(agentActivityLog),
}));

export const agentActivityLogRelations = relations(agentActivityLog, ({ one }) => ({
  task: one(agentTasks, { fields: [agentActivityLog.taskId], references: [agentTasks.id] }),
  academy: one(academies, { fields: [agentActivityLog.academyId], references: [academies.id] }),
}));

export const adminActionQueueRelations = relations(adminActionQueue, ({ one }) => ({
  academy: one(academies, { fields: [adminActionQueue.academyId], references: [academies.id] }),
  relatedUser: one(users, { fields: [adminActionQueue.relatedUserId], references: [users.id], relationName: "admin_queue_related_user" }),
  relatedKid: one(players, { fields: [adminActionQueue.relatedKidId], references: [players.id] }),
  agentTask: one(agentTasks, { fields: [adminActionQueue.agentTaskId], references: [agentTasks.id] }),
  resolver: one(users, { fields: [adminActionQueue.resolvedByUserId], references: [users.id], relationName: "admin_queue_resolver" }),
}));

export const kidProgressSummaryRelations = relations(kidProgressSummary, ({ one }) => ({
  academy: one(academies, { fields: [kidProgressSummary.academyId], references: [academies.id] }),
  kid: one(players, { fields: [kidProgressSummary.kidId], references: [players.id] }),
}));

export const coachKidNotesRelations = relations(coachKidNotes, ({ one }) => ({
  academy: one(academies, { fields: [coachKidNotes.academyId], references: [academies.id] }),
  coach: one(users, { fields: [coachKidNotes.coachUserId], references: [users.id] }),
  kid: one(players, { fields: [coachKidNotes.kidId], references: [players.id] }),
}));

export const parentMessagesRelations = relations(parentMessages, ({ one }) => ({
  academy: one(academies, { fields: [parentMessages.academyId], references: [academies.id] }),
  parent: one(users, { fields: [parentMessages.parentUserId], references: [users.id], relationName: "parent_msg_parent" }),
  kid: one(players, { fields: [parentMessages.kidId], references: [players.id] }),
  resolvedByAgentTask: one(agentTasks, { fields: [parentMessages.resolvedByAgentTaskId], references: [agentTasks.id] }),
  resolvedByUser: one(users, { fields: [parentMessages.resolvedByUserId], references: [users.id], relationName: "parent_msg_resolver" }),
}));

// drizzle-zod insert/select schemas + types.
export const insertAgentTaskSchema = createInsertSchema(agentTasks);
export const agentTaskSchema = createSelectSchema(agentTasks);
export type InsertAgentTask = z.infer<typeof insertAgentTaskSchema>;
export type AgentTask = z.infer<typeof agentTaskSchema>;

export const insertAgentMemorySchema = createInsertSchema(agentMemory);
export const agentMemorySchema = createSelectSchema(agentMemory);
export type InsertAgentMemory = z.infer<typeof insertAgentMemorySchema>;
export type AgentMemory = z.infer<typeof agentMemorySchema>;

export const insertAgentActivityLogSchema = createInsertSchema(agentActivityLog);
export const agentActivityLogSchema = createSelectSchema(agentActivityLog);
export type InsertAgentActivityLog = z.infer<typeof insertAgentActivityLogSchema>;
export type AgentActivityLog = z.infer<typeof agentActivityLogSchema>;

export const insertAdminActionQueueSchema = createInsertSchema(adminActionQueue);
export const adminActionQueueSchema = createSelectSchema(adminActionQueue);
export type InsertAdminActionQueue = z.infer<typeof insertAdminActionQueueSchema>;
export type AdminActionQueueRow = z.infer<typeof adminActionQueueSchema>;

export const insertKidProgressSummarySchema = createInsertSchema(kidProgressSummary);
export const kidProgressSummarySchema = createSelectSchema(kidProgressSummary);
export type InsertKidProgressSummary = z.infer<typeof insertKidProgressSummarySchema>;
export type KidProgressSummary = z.infer<typeof kidProgressSummarySchema>;

export const insertCoachKidNoteSchema = createInsertSchema(coachKidNotes);
export const coachNoteSchema = createSelectSchema(coachKidNotes);
export type InsertCoachKidNote = z.infer<typeof insertCoachKidNoteSchema>;
export type CoachKidNote = z.infer<typeof coachNoteSchema>;

export const insertParentMessageSchema = createInsertSchema(parentMessages);
export const parentMessageSchema = createSelectSchema(parentMessages);
export type InsertParentMessage = z.infer<typeof insertParentMessageSchema>;
export type ParentMessage = z.infer<typeof parentMessageSchema>;

export const insertAgentScheduleSchema = createInsertSchema(agentSchedules);
export const agentScheduleSchema = createSelectSchema(agentSchedules);
export type InsertAgentSchedule = z.infer<typeof insertAgentScheduleSchema>;
export type AgentSchedule = z.infer<typeof agentScheduleSchema>;

export const sessionPerformanceStats = pgTable("session_performance_stats", {
  id: serial("id").primaryKey(),
  sessionLogId: integer("session_log_id").references(() => sessionLogs.id, { onDelete: "cascade" }),
  kidId: integer("kid_id").notNull().references(() => players.id, { onDelete: "cascade" }),
  academyId: integer("academy_id").notNull(),

  ballsBowled: integer("balls_bowled").notNull().default(0),
  bowlWideOutsideOff: integer("bowl_wide_outside_off").notNull().default(0),
  bowlOffStump: integer("bowl_off_stump").notNull().default(0),
  bowlMiddleStump: integer("bowl_middle_stump").notNull().default(0),
  bowlLegStump: integer("bowl_leg_stump").notNull().default(0),
  bowlWideLeg: integer("bowl_wide_leg").notNull().default(0),
  bowlWides: integer("bowl_wides").notNull().default(0),
  bowlNoBalls: integer("bowl_no_balls").notNull().default(0),

  bowlFullToss: integer("bowl_full_toss").notNull().default(0),
  bowlFull: integer("bowl_full").notNull().default(0),
  bowlGoodLength: integer("bowl_good_length").notNull().default(0),
  bowlShortOfLength: integer("bowl_short_of_length").notNull().default(0),
  bowlBouncer: integer("bowl_bouncer").notNull().default(0),
  bowlYorker: integer("bowl_yorker").notNull().default(0),

  batBallsFaced: integer("bat_balls_faced").notNull().default(0),
  batCoverDrive: integer("bat_cover_drive").notNull().default(0),
  batSquareCut: integer("bat_square_cut").notNull().default(0),
  batPullHook: integer("bat_pull_hook").notNull().default(0),
  batFlickSweep: integer("bat_flick_sweep").notNull().default(0),
  batStraightDrive: integer("bat_straight_drive").notNull().default(0),
  batDefended: integer("bat_defended").notNull().default(0),
  batFours: integer("bat_fours").notNull().default(0),
  batSixes: integer("bat_sixes").notNull().default(0),
  batDotBalls: integer("bat_dot_balls").notNull().default(0),
  batRunsScored: integer("bat_runs_scored").notNull().default(0),
  batWickets: integer("bat_wickets").notNull().default(0),

  fieldCatchesAttempted: integer("field_catches_attempted").notNull().default(0),
  fieldCatchesTaken: integer("field_catches_taken").notNull().default(0),
  fieldGroundBalls: integer("field_ground_balls").notNull().default(0),
  fieldGroundClean: integer("field_ground_clean").notNull().default(0),
  fieldThrowsAttempted: integer("field_throws_attempted").notNull().default(0),
  fieldThrowsOnTarget: integer("field_throws_on_target").notNull().default(0),
  fieldRunOutsAttempted: integer("field_run_outs_attempted").notNull().default(0),
  fieldRunOutsSuccess: integer("field_run_outs_success").notNull().default(0),

  fitYoyoLevel: real("fit_yoyo_level"),
  fitYoyoShuttles: integer("fit_yoyo_shuttles"),
  fitSprint20mSec: real("fit_sprint_20m_sec"),
  fitShuttleRunSec: real("fit_shuttle_run_sec"),
  fitPushUps: integer("fit_push_ups"),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

export const sessionPerformanceStatsRelations = relations(sessionPerformanceStats, ({ one }) => ({
  sessionLog: one(sessionLogs, {
    fields: [sessionPerformanceStats.sessionLogId],
    references: [sessionLogs.id],
  }),
  kid: one(players, {
    fields: [sessionPerformanceStats.kidId],
    references: [players.id],
  }),
}));

export const insertSessionPerformanceStatsSchema = createInsertSchema(sessionPerformanceStats);
export const selectSessionPerformanceStatsSchema = createSelectSchema(sessionPerformanceStats);
export type InsertSessionPerformanceStats = z.infer<typeof insertSessionPerformanceStatsSchema>;
export type SessionPerformanceStats = z.infer<typeof selectSessionPerformanceStatsSchema>;